import type { Schemas } from "@neio/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Save } from "lucide-react";
import { useId, useState } from "react";

import { ApiError, apiGet, request, switchedOff } from "../../lib/http";
import { FeatureOff } from "../FeatureOff";
import { ErrorState, Loading } from "../States";
import { utcTime } from "./format";

export type Threshold = Schemas["ReviewThresholdView"];

const THRESHOLD_KEY = ["admin", "review-threshold"] as const;
const pct = (fraction: number) => Math.round(fraction * 100);

export function fetchThreshold(): Promise<Threshold> {
  return apiGet("/api/v1/settings/review-threshold");
}

/** `PUT /settings/review-threshold`. Admin only; 409 while the environment keeps it fixed. */
export function saveThreshold(value: number): Promise<Threshold> {
  return request<Threshold>("/settings/review-threshold", { method: "PUT", body: JSON.stringify({ value }) });
}

/** Why a typed percent cannot be sent, or null when it can. */
export function thresholdInputProblem(raw: string, view: Threshold): string | null {
  const lo = pct(view.min);
  const hi = pct(view.max);
  const n = Number(raw);
  if (raw.trim() === "" || !Number.isInteger(n) || n < lo || n > hi) return `Pick a whole percent from ${lo}% to ${hi}%.`;
  return null;
}

/** A refused save, said so a person knows what to do next. */
export function saveProblem(error: unknown): string {
  if (error instanceof ApiError && error.status === 409) {
    return `Not editable on this environment. ${error.message.charAt(0).toUpperCase()}${error.message.slice(1)}.`;
  }
  return error instanceof Error ? error.message : String(error);
}

export function ThresholdPanel({ view, onSave }: { view: Threshold; onSave: (value: number) => Promise<unknown> }) {
  const [draft, setDraft] = useState(String(pct(view.value)));
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inputId = useId();
  const problemId = useId();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const bad = thresholdInputProblem(draft, view);
    if (bad) return setProblem(bad);
    setSaving(true);
    setProblem(null);
    try {
      await onSave(Number(draft) / 100);
    } catch (error) {
      setProblem(saveProblem(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card stack">
      <div>
        <div className="section-title">Review threshold</div>
        <p className="muted">
          A field read with less confidence than this goes to a person for review. Autonomous package runs use it as their floor.
        </p>
      </div>
      <div className="row">
        <span className="spend-big num">{pct(view.value)}%</span>
        <span className="muted">
          default {pct(view.default)}%, allowed {pct(view.min)}% to {pct(view.max)}%
          {view.changed_by && <>. Set by {view.changed_by}{view.changed_at ? ` on ${utcTime(view.changed_at)}` : ""}</>}
        </span>
      </div>
      {view.editable ? (
        <form className="filters" onSubmit={submit} noValidate aria-label="Change the review threshold">
          <label className="field" htmlFor={inputId}>
            <span>New threshold, percent</span>
            <input id={inputId} type="number" inputMode="numeric" min={pct(view.min)} max={pct(view.max)} step={1}
              value={draft} onChange={(e) => setDraft(e.target.value)}
              aria-invalid={problem ? true : undefined} aria-describedby={problem ? problemId : undefined} />
          </label>
          <button type="submit" disabled={saving}><Save size={14} aria-hidden="true" /> {saving ? "Saving" : "Save"}</button>
          {problem && <p id={problemId} className="field-problem" role="alert">{problem}</p>}
        </form>
      ) : (
        <p className="row muted" role="status">
          <Lock size={14} aria-hidden="true" />
          Not editable on this environment. It stays fixed until the A12 calibration report is published and
          REVIEW_THRESHOLD_EDITABLE is turned on.
        </p>
      )}
      <p className="muted">Every change is written to the audit log. Only later extractions and runs use a new value.</p>
    </div>
  );
}

export function ThresholdTab() {
  const client = useQueryClient();
  const query = useQuery({ queryKey: THRESHOLD_KEY, queryFn: fetchThreshold });
  const off = switchedOff(query.error, [404]);
  if (query.isPending) return <Loading what="Reading the review threshold" />;
  if (off !== null) return <FeatureOff feature="The review threshold setting" reason={off} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  return (
    <ThresholdPanel
      key={query.data.value}
      view={query.data}
      onSave={async (value) => {
        const saved = await saveThreshold(value);
        client.setQueryData(THRESHOLD_KEY, saved);
        await client.invalidateQueries({ queryKey: ["admin", "audit"] });
      }}
    />
  );
}
